import { useMutation, useQueryClient } from '@tanstack/react-query';
import { t } from '@lingui/core/macro';
import { client } from './apiClient';
import { useToast } from '../hooks/useToast';
import {
  BookmarkActionParams,
  MoveToFolderParams,
  TagActionParams,
  BookmarkAction,
  BookmarkResponse,
  BookmarkCollectionResponse,
  Bookmark,
  Note,
  ReportProblemParams,
} from '../types/api';

export interface LikeBookmarkParams extends BookmarkActionParams {
  action?: 'like' | 'unlike';
}

// Helper function to compare bookmark IDs (handles string/number differences)
function bookmarkIdsMatch(id1: string | number, id2: string | number): boolean {
  return String(id1) === String(id2);
}

// Helper function to find a bookmark in bookmark list caches
function findBookmarkInLists(
  queryClient: ReturnType<typeof useQueryClient>,
  bookmarkId: string,
): Bookmark | undefined {
  const bookmarkListQueries = queryClient.getQueriesData({
    predicate: (query) => {
      const key = query.queryKey;
      return Array.isArray(key) && (key[0] === 'bookmarks' || key[0] === 'search');
    },
  });

  for (const [, data] of bookmarkListQueries) {
    if (data && typeof data === 'object' && 'bookmarks' in data) {
      const bookmarkList = (data as BookmarkCollectionResponse).bookmarks;
      const foundBookmark = bookmarkList.find((b) => bookmarkIdsMatch(b.id, bookmarkId));
      if (foundBookmark) {
        return foundBookmark;
      }
    }
  }
  return undefined;
}

// Helper function to invalidate bookmark-related queries
function invalidateBookmarkQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  bookmarkId: string,
) {
  const bookmarkIdString = String(bookmarkId);
  queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
  queryClient.invalidateQueries({ queryKey: ['bookmark', bookmarkIdString] });
  queryClient.invalidateQueries({ queryKey: ['currentUser'] });
}

// Refresh the search result caches after a *content* edit (tags, notes, title).
// Search is its own cache that `invalidateBookmarkQueries` deliberately leaves
// alone — membership actions (archive/like/move) must not disturb the search
// list — so content mutations opt in explicitly to keep search rows in sync.
function invalidateSearchResults(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['search'] });
}

// Optimistically remove a bookmark from all list caches,
// returning a snapshot so the caller can roll back on error.
//
// `includeSearch` also removes the bookmark from the `['search']` result caches.
// This is deliberately opt-in and used only by delete: a deleted bookmark is
// gone everywhere, so it should drop out of the search results too. Reversible
// actions (archive/unarchive) intentionally leave the search list untouched —
// their result still matches the query, just in a different section.
async function optimisticallyRemoveFromLists(
  queryClient: ReturnType<typeof useQueryClient>,
  bookmarkId: string,
  { includeSearch = false }: { includeSearch?: boolean } = {},
) {
  const bookmarkIdString = String(bookmarkId);
  const listPrefixes = includeSearch ? ['bookmarks', 'search'] : ['bookmarks'];

  await queryClient.cancelQueries({ queryKey: ['bookmarks'] });
  if (includeSearch) {
    await queryClient.cancelQueries({ queryKey: ['search'] });
  }

  const previousBookmarkLists = queryClient.getQueriesData({
    predicate: (query) => {
      const key = query.queryKey;
      return Array.isArray(key) && listPrefixes.includes(key[0] as string);
    },
  });

  previousBookmarkLists.forEach(([queryKey]) => {
    queryClient.setQueryData(queryKey, (old: BookmarkCollectionResponse | undefined) => {
      if (!old?.bookmarks) return old;
      const bookmarks = old.bookmarks.filter(
        (b) => !bookmarkIdsMatch(b.id, bookmarkIdString),
      );
      if (bookmarks.length === old.bookmarks.length) return old;
      // Search responses carry a total `count`; keep it in step with the list so
      // the header count doesn't drift after a delete.
      const withCount = old as BookmarkCollectionResponse & { count?: number };
      const count =
        typeof withCount.count === 'number'
          ? Math.max(0, withCount.count - 1)
          : withCount.count;
      return { ...old, bookmarks, ...(count !== undefined ? { count } : {}) };
    });
  });

  return { previousBookmarkLists };
}

// Remove bookmarks from the `['search']` result caches. Search results are their
// own cache that delete's `['bookmarks']` invalidation doesn't touch, so deleted
// rows must be filtered out directly or they'd reappear (the batch path animates
// them out but never refetches search). Delete-only by design — reversible
// actions leave the search list alone.
export function removeBookmarksFromSearchCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  bookmarkIds: Array<string | number>,
) {
  const ids = new Set(bookmarkIds.map(String));
  const searchQueries = queryClient.getQueriesData({
    predicate: (query) => {
      const key = query.queryKey;
      return Array.isArray(key) && key[0] === 'search';
    },
  });

  searchQueries.forEach(([queryKey]) => {
    queryClient.setQueryData(queryKey, (old: BookmarkCollectionResponse | undefined) => {
      if (!old?.bookmarks) return old;
      const bookmarks = old.bookmarks.filter((b) => !ids.has(String(b.id)));
      const removed = old.bookmarks.length - bookmarks.length;
      if (removed === 0) return old;
      const withCount = old as BookmarkCollectionResponse & { count?: number };
      const count =
        typeof withCount.count === 'number'
          ? Math.max(0, withCount.count - removed)
          : withCount.count;
      return { ...old, bookmarks, ...(count !== undefined ? { count } : {}) };
    });
  });
}

// Bookmark State Actions
function useArchiveBookmark() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async ({ bookmarkId }: BookmarkActionParams) => {
      const action: BookmarkAction = {
        bookmark_id: bookmarkId,
        action: 'archive',
      };
      await client.bookmarkActions([action]);
      return bookmarkId;
    },
    onMutate: async ({ bookmarkId }) => {
      return optimisticallyRemoveFromLists(queryClient, bookmarkId);
    },
    onError: (_err, _vars, context) => {
      context?.previousBookmarkLists?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      showToast(t`Sorry, there was an error. Please try again.`, 'error');
    },
    onSuccess: (bookmarkId) => {
      invalidateBookmarkQueries(queryClient, bookmarkId);
    },
  });
}

function useDeleteBookmark() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async ({ bookmarkId }: BookmarkActionParams) => {
      const action: BookmarkAction = {
        bookmark_id: bookmarkId,
        action: 'delete',
      };
      await client.bookmarkActions([action]);
      return bookmarkId;
    },
    onMutate: async ({ bookmarkId }) => {
      // Delete removes the bookmark everywhere, including from search results.
      return optimisticallyRemoveFromLists(queryClient, bookmarkId, {
        includeSearch: true,
      });
    },
    onError: (_err, _vars, context) => {
      context?.previousBookmarkLists?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      showToast(t`Sorry, there was an error. Please try again.`, 'error');
    },
    onSuccess: (bookmarkId) => {
      invalidateBookmarkQueries(queryClient, bookmarkId);
    },
  });
}

function useLikeBookmark() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async ({ bookmarkId, action = 'like' }: LikeBookmarkParams) => {
      const bookmarkAction: BookmarkAction = {
        bookmark_id: bookmarkId,
        action,
      };
      await client.bookmarkActions([bookmarkAction]);
      return { bookmarkId, action };
    },
    onMutate: async ({ bookmarkId, action = 'like' }) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['bookmark', String(bookmarkId)] });

      // Snapshot the previous value
      const previousBookmark = queryClient.getQueryData(['bookmark', String(bookmarkId)]);

      // Optimistically update to the new value
      queryClient.setQueryData(
        ['bookmark', String(bookmarkId)],
        (old: BookmarkResponse | undefined) => {
          if (!old) return old;

          return {
            ...old,
            bookmark: {
              ...old.bookmark,
              liked: action === 'like',
            },
          };
        },
      );

      // Return a context object with the snapshotted value
      return { previousBookmark };
    },
    onError: (err, { bookmarkId }, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousBookmark) {
        queryClient.setQueryData(
          ['bookmark', String(bookmarkId)],
          context.previousBookmark,
        );
      }
      showToast(t`Sorry, there was an error. Please try again.`, 'error');
      console.error('Failed to update bookmark like status:', err);
    },
    onSuccess: ({ bookmarkId }) => {
      invalidateBookmarkQueries(queryClient, bookmarkId);
    },
  });
}

function useMoveToFolder() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async ({ bookmarkId, folderId }: MoveToFolderParams) => {
      const action: BookmarkAction = {
        bookmark_id: bookmarkId,
        action: 'move',
        folder_id: folderId,
      };
      await client.bookmarkActions([action]);
      return { bookmarkId, folderId };
    },
    onError: () => {
      showToast(t`Sorry, there was an error. Please try again.`, 'error');
    },
    onSuccess: ({ bookmarkId }) => {
      invalidateBookmarkQueries(queryClient, bookmarkId);
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
  });
}

function useUnarchiveBookmark() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async ({ bookmarkId }: BookmarkActionParams) => {
      const action: BookmarkAction = {
        bookmark_id: bookmarkId,
        action: 'home',
      };
      await client.bookmarkActions([action]);
      return bookmarkId;
    },
    onMutate: async ({ bookmarkId }) => {
      return optimisticallyRemoveFromLists(queryClient, bookmarkId);
    },
    onError: (_err, _vars, context) => {
      context?.previousBookmarkLists?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      showToast(t`Sorry, there was an error. Please try again.`, 'error');
    },
    onSuccess: (bookmarkId) => {
      invalidateBookmarkQueries(queryClient, bookmarkId);
    },
  });
}

function useUpdateTags() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async ({
      bookmarkId,
      addTags = [],
      removeTags = [],
    }: TagActionParams) => {
      const action: BookmarkAction = {
        bookmark_id: bookmarkId,
        action: 'tags',
        add_tags: addTags.map((tag) => ({ name: tag })),
        remove_tags: removeTags.map((tag) => ({ name: tag })),
      };
      await client.bookmarkActions([action]);
      return { bookmarkId, addTags, removeTags };
    },
    onMutate: async ({ bookmarkId, addTags = [], removeTags = [] }) => {
      const bookmarkIdString = String(bookmarkId);

      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['bookmark', bookmarkIdString] });
      await queryClient.cancelQueries({ queryKey: ['bookmarks'] });
      await queryClient.cancelQueries({ queryKey: ['search'] });

      // Snapshot the previous values
      let previousBookmark: BookmarkResponse | undefined = queryClient.getQueryData([
        'bookmark',
        bookmarkIdString,
      ]);

      // Find all bookmark list queries — both the section lists (['bookmarks', …])
      // and the search results (['search', …]) — so a tag change reflects wherever
      // the bookmark is currently shown.
      const previousBookmarks = queryClient.getQueriesData({
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && (key[0] === 'bookmarks' || key[0] === 'search');
        },
      });

      // If bookmark cache doesn't exist, try to find it in bookmark list caches and create it
      if (!previousBookmark) {
        const foundBookmark = findBookmarkInLists(queryClient, bookmarkIdString);
        if (foundBookmark) {
          previousBookmark = { bookmark: foundBookmark };
          queryClient.setQueryData(['bookmark', bookmarkIdString], previousBookmark);
        }
      }

      // Helper function to update tags in a bookmark
      const updateBookmarkTags = (bookmark: Bookmark): Bookmark => {
        const currentTags = bookmark.tags || [];

        // Add new tags (create temporary tag objects)
        const newTags = addTags.map((tagName) => ({
          id: `temp-${Date.now()}-${Math.random()}`, // Temporary ID
          name: tagName,
          slug: tagName.toLowerCase().replace(/\s+/g, '-'),
          count: 1,
        }));

        // Remove tags by name
        const filteredTags = currentTags.filter((tag) => !removeTags.includes(tag.name));

        // Combine existing filtered tags with new tags
        const updatedTags = [...filteredTags, ...newTags];

        return {
          ...bookmark,
          tags: updatedTags,
        };
      };

      // Optimistically update the bookmark cache
      queryClient.setQueryData(
        ['bookmark', bookmarkIdString],
        (old: BookmarkResponse | undefined) => {
          if (!old?.bookmark) {
            // If we found it in bookmark lists earlier, use that and update it
            if (previousBookmark?.bookmark) {
              const updatedBookmark = updateBookmarkTags(previousBookmark.bookmark);
              return { bookmark: updatedBookmark };
            }
            // Last resort: try to find it in bookmark lists right now
            const foundBookmark = findBookmarkInLists(queryClient, bookmarkIdString);
            if (foundBookmark) {
              const updatedBookmark = updateBookmarkTags(foundBookmark);
              return { bookmark: updatedBookmark };
            }
            return old;
          }
          const updated = {
            ...old,
            bookmark: updateBookmarkTags(old.bookmark),
          };
          return updated;
        },
      );

      // Optimistically update all bookmark list caches
      previousBookmarks.forEach(([queryKey, data]) => {
        if (data && typeof data === 'object' && 'bookmarks' in data) {
          queryClient.setQueryData(
            queryKey,
            (old: BookmarkCollectionResponse | undefined) => {
              if (!old?.bookmarks) return old;
              return {
                ...old,
                bookmarks: old.bookmarks.map((b) =>
                  bookmarkIdsMatch(b.id, bookmarkIdString) ? updateBookmarkTags(b) : b,
                ),
              };
            },
          );
        }
      });

      // Return a context object with the snapshotted values
      return { previousBookmark, previousBookmarks };
    },
    onError: (err, { bookmarkId }, context) => {
      const bookmarkIdString = String(bookmarkId);

      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousBookmark) {
        queryClient.setQueryData(
          ['bookmark', bookmarkIdString],
          context.previousBookmark,
        );
      }
      // Roll back bookmark list caches
      if (context?.previousBookmarks) {
        context.previousBookmarks.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      showToast(t`Sorry, there was an error. Please try again.`, 'error');
      console.error('Failed to update tags:', err);
    },
    onSuccess: (result) => {
      const bookmarkIdString = String(result.bookmarkId);

      // Invalidate queries to ensure we have the latest data from the server
      // The optimistic update already shows the changes, but we want to sync with server
      invalidateBookmarkQueries(queryClient, bookmarkIdString);
      invalidateSearchResults(queryClient); // Search rows show tags too — keep them in sync
      queryClient.invalidateQueries({ queryKey: ['currentUser'] }); // Refresh user tags list when new tags are created
    },
  });
}

function useReportProblem() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async ({ bookmarkId, problem }: ReportProblemParams) => {
      const action: BookmarkAction = {
        bookmark_id: bookmarkId,
        action: 'report_problem',
        problem,
      };
      await client.bookmarkActions([action]);
      return bookmarkId;
    },
    onError: () => {
      showToast(t`Sorry, there was an error. Please try again.`, 'error');
    },
    onSuccess: (bookmarkId) => {
      // Ensure bookmarkId is a string for cache consistency
      const bookmarkIdString = String(bookmarkId);
      // Invalidate affected queries to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
      queryClient.invalidateQueries({ queryKey: ['bookmark', bookmarkIdString] });
    },
  });
}

// Reading Progress Actions
function useProgressMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ bookmarkId, progress }: { bookmarkId: string; progress: number }) =>
      client.progress(bookmarkId, progress),
    onSuccess: (_, { bookmarkId, progress }) => {
      // Update the bookmark cache optimistically instead of invalidating
      queryClient.setQueryData(
        ['bookmark', bookmarkId],
        (oldData: BookmarkResponse | undefined) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            bookmark: {
              ...oldData.bookmark,
              progress,
              progressTimestamp: Date.now() / 1000, // Current timestamp
            },
          };
        },
      );
    },
  });
}

// Highlight Actions
function useCreateHighlight() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      bookmarkId,
      params,
    }: {
      bookmarkId: string;
      params: { text: string; position?: number; note?: string };
    }) => client.createHighlight(bookmarkId, params),
    onMutate: async ({ bookmarkId, params }) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['bookmark', String(bookmarkId)] });

      // Snapshot the previous value
      const previousBookmark = queryClient.getQueryData(['bookmark', String(bookmarkId)]);

      // Optimistically update to the new value
      queryClient.setQueryData(
        ['bookmark', String(bookmarkId)],
        (old: BookmarkResponse | undefined) => {
          if (!old) return old;

          // Create a temporary highlight ID for optimistic update
          const tempHighlightId = `temp-${Date.now()}`;

          const newHighlight: Note = {
            id: tempHighlightId,
            text: params.text,
            note: params.note || '',
            position: params.position || 0,
            time: Date.now() / 1000, // Current timestamp
            bookmark_id: String(bookmarkId),
          };

          const updatedBookmark = {
            ...old,
            bookmark: {
              ...old.bookmark,
              notes: [...(old.bookmark?.notes || []), newHighlight],
            },
          };

          return updatedBookmark;
        },
      );

      // Return a context object with the snapshotted value
      return { previousBookmark };
    },
    onError: (err, { bookmarkId }, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousBookmark) {
        queryClient.setQueryData(
          ['bookmark', String(bookmarkId)],
          context.previousBookmark,
        );
      }
      console.error('Failed to create highlight:', err);
    },
    onSuccess: (_, { bookmarkId }) => {
      // Invalidate the query to refetch the proper bookmark data with the real highlight ID
      queryClient.invalidateQueries({ queryKey: ['bookmark', String(bookmarkId)] });
      queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
      invalidateSearchResults(queryClient); // Search rows show note indicators too
    },
  });
}

function useDeleteHighlight() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      highlightId,
      formKey,
    }: {
      highlightId: string;
      bookmarkId: string;
      formKey?: string;
    }) => {
      return client.deleteHighlight(highlightId, formKey);
    },
    onMutate: async ({ highlightId, bookmarkId }) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['bookmark', String(bookmarkId)] });

      // Snapshot the previous value
      const previousBookmark = queryClient.getQueryData(['bookmark', String(bookmarkId)]);

      // Optimistically update to the new value
      queryClient.setQueryData(
        ['bookmark', String(bookmarkId)],
        (old: BookmarkResponse | undefined) => {
          if (!old) return old;

          return {
            ...old,
            bookmark: {
              ...old.bookmark,
              notes:
                old.bookmark?.notes?.filter((note: Note) => note.id !== highlightId) ||
                [],
            },
          };
        },
      );

      // Return a context object with the snapshotted value
      return { previousBookmark };
    },
    onError: (err, { bookmarkId }, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousBookmark) {
        queryClient.setQueryData(
          ['bookmark', String(bookmarkId)],
          context.previousBookmark,
        );
      }
      console.error('Failed to delete highlight:', err);
    },
    onSettled: (_, __, { bookmarkId }) => {
      // Always refetch after error or success to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: ['bookmark', String(bookmarkId)] });
      queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
      invalidateSearchResults(queryClient); // Search rows show note indicators too
    },
  });
}

function useUpdateHighlightNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      highlightId,
      note,
      formKey,
    }: {
      highlightId: string;
      note: string;
      formKey: string;
    }) => client.updateHighlightNote(highlightId, note, formKey),
    onMutate: async ({ highlightId, note }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['bookmark'] });

      // Snapshot the previous value
      const previousBookmarks = queryClient.getQueriesData({ queryKey: ['bookmark'] });

      // Optimistically update all bookmark queries that might contain this highlight
      queryClient.setQueriesData(
        { queryKey: ['bookmark'] },
        (old: BookmarkResponse | undefined) => {
          if (!old) return old;
          return {
            ...old,
            bookmark: {
              ...old.bookmark,
              notes:
                old.bookmark?.notes?.map((noteItem: Note) =>
                  noteItem.id === highlightId ? { ...noteItem, note } : noteItem,
                ) || [],
            },
          };
        },
      );

      // Return a context object with the snapshotted value
      return { previousBookmarks };
    },
    onError: (err, _, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousBookmarks) {
        context.previousBookmarks.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      console.error('Failed to update highlight note:', err);
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: ['bookmark'] });
      queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
      invalidateSearchResults(queryClient); // Search rows show note indicators too
    },
  });
}

export interface UpdateBookmarkParams {
  bookmarkId: string;
  title: string;
  url: string;
  description?: string;
}

function useUpdateBookmark() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async ({ bookmarkId, title, url, description }: UpdateBookmarkParams) => {
      const response = await client.updateBookmark(bookmarkId, {
        title,
        url,
        description: description || '',
      });
      return { bookmarkId, title, url, description, response };
    },
    onMutate: async ({ bookmarkId, title, url, description }) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['bookmark', String(bookmarkId)] });

      // Snapshot the previous value
      const previousBookmark = queryClient.getQueryData(['bookmark', String(bookmarkId)]);

      // Optimistically update to the new value
      queryClient.setQueryData(
        ['bookmark', String(bookmarkId)],
        (old: BookmarkResponse | undefined) => {
          if (!old) return old;

          return {
            ...old,
            bookmark: {
              ...old.bookmark,
              title,
              url,
              description: description || old.bookmark.description,
            },
          };
        },
      );

      // Return a context object with the snapshotted value
      return { previousBookmark };
    },
    onError: (err, { bookmarkId }, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousBookmark) {
        queryClient.setQueryData(
          ['bookmark', String(bookmarkId)],
          context.previousBookmark,
        );
      }
      showToast(t`Sorry, there was an error. Please try again.`, 'error');
      console.error('useUpdateBookmark onError:', { error: err });
    },
    onSuccess: (result) => {
      invalidateBookmarkQueries(queryClient, result.bookmarkId);
      invalidateSearchResults(queryClient); // Title/description also render in search rows
    },
  });
}

export {
  useArchiveBookmark,
  useDeleteBookmark,
  useLikeBookmark,
  useMoveToFolder,
  useUnarchiveBookmark,
  useUpdateTags,
  useUpdateBookmark,
  useReportProblem,
  useProgressMutation,
  useCreateHighlight,
  useDeleteHighlight,
  useUpdateHighlightNote,
};
